# MVX API

Look up MVX vaccine manufacturer codes from the CDC.

## Overview

MVX (Manufacturers of Vaccines) codes identify vaccine manufacturers. These codes are used in conjunction with CVX codes to uniquely identify vaccine products.

## Endpoints

| Method | Path | Description |
|--------|------|-------------|
| GET | `/v1/mvx/:code` | Single MVX lookup |
| POST | `/v1/mvx/_batch` | Batch lookup (up to 100) |
| GET | `/v1/mvx/search` | Search manufacturers by name |
| GET | `/v1/mvx/shapes` | List available response shapes |

Manufacturer names are the usual starting point here — CVX NDC crosswalks carry a
`manufacturer` string but no MVX code, so resolving one to the other goes through
the [MVX Search API](/docs/search-apis/mvx).

## Single Lookup

<ApiExample
  endpoint="/v1/mvx/PFR"
  method="GET"
  title="Look up Pfizer vaccine manufacturer"
  response={{
    "data": {
      "code": "PFR",
      "code_system": "http://hl7.org/fhir/sid/mvx",
      "display": "Pfizer, Inc",
      "fhir_coding": {
        "code": "PFR",
        "display": "Pfizer, Inc",
        "system": "http://hl7.org/fhir/sid/mvx"
      },
      "last_updated_by_cdc": "2020-10-30",
      "manufacturer_name": "Pfizer, Inc",
      "notes": "COVID-19 vaccine in co-development with BioNTech. Pfizer includes Wyeth-Lederle Vaccines and Pediatrics, Wyeth Laboratories, Lederle Laboratories, and Praxis Biologics",
      "status": "active"
    },
    "meta": {
      "legal": {
        "attribution_required": false,
        "citation": "CDC National Center for Immunization and Respiratory Diseases. Accessed 2026-01-14 via FHIRfly.",
        "license": "public_domain",
        "source_name": "CDC Immunization"
      }
    }
  }}
/>

## Common MVX Codes

| Code | Manufacturer |
|------|--------------|
| PFR | Pfizer, Inc |
| MOD | Moderna US, Inc |
| JSN | Janssen Products, LP |
| SKB | GlaxoSmithKline |
| MSD | Merck Sharp & Dohme |

## Batch Lookup

Look up multiple MVX codes in a single request (up to 100):

<ApiExample
  endpoint="/v1/mvx/_batch"
  method="POST"
  title="Look up multiple manufacturers"
  requestBody={{
    "codes": ["PFR", "MOD", "SKB"]
  }}
  response={{
    "count": 3,
    "results": [
      {
        "input": "PFR",
        "code": "PFR",
        "status": "ok",
        "data": { "display": "Pfizer, Inc", "status": "active" }
      },
      {
        "input": "MOD",
        "code": "MOD",
        "status": "ok",
        "data": { "display": "Moderna US, Inc", "status": "active" }
      },
      {
        "input": "SKB",
        "code": "SKB",
        "status": "ok",
        "data": { "display": "GlaxoSmithKline", "status": "active" }
      }
    ],
    "meta": {
      "legal": {
        "license": "public_domain"
      }
    }
  }}
/>

### Batch with `shape=full`

When using `shape=full` on batch requests, the response includes `meta.source` with provenance information:

```
POST /v1/mvx/_batch?shape=full
```

```json
{
  "count": 3,
  "results": [
    {
      "input": "PFR",
      "code": "PFR",
      "status": "ok",
      "data": { "display": "Pfizer, Inc", "status": "active" }
    }
  ],
  "meta": {
    "source": {
      "name": "CDC Immunization",
      "url": "https://www2a.cdc.gov/vaccines/iis/iisstandards/vaccines.asp?rpt=mvx",
      "version": "monthly",
      "fhirfly_updated_at": "2026-03-01T03:00:00Z"
    },
    "legal": {
      "license": "public_domain",
      "attribution_required": false
    }
  }
}
```

## Response Shapes

Control the level of detail in responses with the `shape` query parameter:

| Shape | Description |
|-------|-------------|
| `compact` | Code, display, status |
| `standard` | + manufacturer_name, notes, fhir_coding |
| `full` | + ingest metadata |

```bash
# Minimal data
GET /v1/mvx/PFR?shape=compact

# Standard data (default)
GET /v1/mvx/PFR?shape=standard

# Full data with provenance
GET /v1/mvx/PFR?shape=full
```

See [Response Shapes](/docs/response-shapes) for field details.

## Required Scopes

- `mvx.read` - Single and batch lookups
- `mvx.search` - Search endpoint

## See Also

- [MVX Search API](/docs/search-apis/mvx) — Search manufacturers by name
- [CVX API](/docs/value-apis/cvx) — Vaccine codes, including NDC crosswalks

<DocsNextStep />
